import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Wrench, Calendar, ClipboardList, CheckCircle, PlusCircle, ArrowRight, Loader2 } from "lucide-react";
import { API_URL } from "@/config";
import { Card } from "@/components/ui/card";

interface Stats { open: number; inProgress: number; completedToday: number; apptToday: number }
interface RecentRO {
    ro_id: number; status: string; vehicle_desc: string | null; total_amount: string | null; created_at: string;
    appointment: { customer_name: string | null; service_type: string | null } | null;
}

const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

export default function ServiceDashboard() {
    const [staff,   setStaff]   = useState<StaffUser | null>(null);
    const [stats,   setStats]   = useState<Stats | null>(null);
    const [recent,  setRecent]  = useState<RecentRO[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); }),
            axios.get(`${API_URL}/repair-shops/staff/stats`, { withCredentials: true })
                .then((r) => setStats(r.data.stats))
                .catch(() => null),
            axios.get(`${API_URL}/repair-shops/staff/repair-orders`, { withCredentials: true })
                .then((r) => setRecent((r.data.repair_orders ?? []).slice(0, 6)))
                .catch(() => null),
        ]).finally(() => setLoading(false));
    }, []);

    const roleLabel =
        staff?.role === "service_tech"    ? "Technician" :
        staff?.role === "service_manager" ? "Service Manager" : "Service Advisor";

    const statCards = [
        { label: "Open ROs",          value: stats?.open          ?? "—", icon: ClipboardList, color: "text-orange-500" },
        { label: "In Progress",        value: stats?.inProgress    ?? "—", icon: Wrench,        color: "text-blue-500"   },
        { label: "Invoiced Today",     value: stats?.completedToday ?? "—", icon: CheckCircle,  color: "text-green-500"  },
        { label: "Appts Today",        value: stats?.apptToday     ?? "—", icon: Calendar,      color: "text-violet-500" },
    ];

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">
                    {staff ? `Welcome, ${staff.first_name}` : "Service Dashboard"}
                </h1>
                {staff && <p className="text-muted-foreground text-sm mt-1">{roleLabel} · Service Portal</p>}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, value, icon: Icon, color }) => (
                    <Card key={label} className="p-4 flex items-center gap-4">
                        <Icon className={`h-8 w-8 ${color}`} />
                        <div>
                            <p className="text-xs text-muted-foreground">{label}</p>
                            <p className="text-2xl font-bold">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : value}</p>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="flex flex-wrap gap-3">
                <Link to="/service/repair-orders/new" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition">
                    <PlusCircle className="h-4 w-4" />New Repair Order
                </Link>
                <Link to="/service/repair-orders" className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition">
                    <ClipboardList className="h-4 w-4" />All Repair Orders
                </Link>
                <Link to="/service/appointments" className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition">
                    <Calendar className="h-4 w-4" />Appointments
                </Link>
            </div>

            <Card className="p-5">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold">Recent Repair Orders</h2>
                    <Link to="/service/repair-orders" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : recent.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No repair orders yet.</p>
                ) : (
                    <div className="divide-y">
                        {recent.map((ro) => (
                            <Link key={ro.ro_id} to={`/service/repair-orders/${ro.ro_id}`} className="flex items-center justify-between py-3 hover:bg-muted/30 px-2 rounded-lg transition-colors">
                                <div>
                                    <p className="text-sm font-medium">
                                        RO #{ro.ro_id}
                                        {ro.appointment?.customer_name ? ` · ${ro.appointment.customer_name}` : ""}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {ro.vehicle_desc ?? ro.appointment?.service_type ?? "—"}
                                        {" · "}{new Date(ro.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {ro.total_amount && <span className="text-sm font-medium">${ro.total_amount}</span>}
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                                        {ro.status}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
